import { fameIndexColor, fameIntegrityBand, type ColorKey } from '@fametrue/shared';

/**
 * Colori esatti dell'Allegato A per gli indici.
 *
 * Le SOGLIE non stanno qui: vivono in `@fametrue/shared` e sono condivise col
 * backend. Qui c'è solo la resa grafica, cioè la mappa fascia → colore, presa
 * dalle funzioni `ftColor`, `indexHex` e affini del riferimento.
 *
 * Attenzione a non "uniformare" queste mappe: il riferimento usa colori diversi
 * per la stessa fascia a seconda dell'indice. Il FameIndex "orange" è #EF9F27
 * (ambra), il FameIntegrity "orange" è #D85A30. È una scelta sua, non un errore.
 */

/** `ftColor` — colore dell'anello FameIntegrity, per fascia. */
const INTEGRITY_RING: Record<number, string> = {
  [-1]: '#9B9590',
  0: '#A32D2D',
  1: '#D85A30',
  2: '#EF9F27',
  3: '#2A9D8F',
};

/** `ftColorNavy` — variante ad alto contrasto per il pannello navy. */
const INTEGRITY_ON_NAVY: Record<number, string> = {
  [-1]: '#9B9590',
  0: '#FF8B7A',
  1: '#FFB088',
  2: '#FFCB6E',
  3: '#6FDBC0',
};

/** `ftBg` / `ftFg` — pillola sotto l'anello. */
const INTEGRITY_PILL_BG: Record<number, string> = {
  [-1]: '#F0EFEC',
  0: '#FDF0EE',
  1: '#FDE8E0',
  2: '#FFF8E6',
  3: '#E8F5F0',
};

const INTEGRITY_PILL_FG: Record<number, string> = {
  [-1]: '#9B9590',
  0: '#A32D2D',
  1: '#D85A30',
  2: '#C47D0A',
  3: '#1A7A6A',
};

export function integrityRingColor(value: number | null): string {
  return INTEGRITY_RING[fameIntegrityBand(value).tier] ?? '#9B9590';
}

export function integrityNavyColor(value: number | null): string {
  return INTEGRITY_ON_NAVY[fameIntegrityBand(value).tier] ?? '#9B9590';
}

export function integrityPillBg(value: number | null): string {
  return INTEGRITY_PILL_BG[fameIntegrityBand(value).tier] ?? '#F0EFEC';
}

export function integrityPillFg(value: number | null): string {
  return INTEGRITY_PILL_FG[fameIntegrityBand(value).tier] ?? '#9B9590';
}

/** `indexHex` — nota: la fascia "orange" del FameIndex rende ambra, non arancio. */
const INDEX_HEX: Record<ColorKey, string> = {
  red: '#A32D2D',
  orange: '#EF9F27',
  amber: '#EF9F27',
  green: '#2A9D8F',
  gray: '#9B9590',
};

const INDEX_PILL_BG: Record<ColorKey, string> = {
  red: 'var(--red-light)',
  orange: 'var(--amber-light)',
  amber: 'var(--amber-light)',
  green: 'var(--green-light)',
  gray: 'var(--cream)',
};

const INDEX_PILL_FG: Record<ColorKey, string> = {
  red: 'var(--red-dark)',
  orange: '#8B6914',
  amber: '#8B6914',
  green: 'var(--green-dark)',
  gray: 'var(--text-3)',
};

export function indexHex(value: number | null): string {
  return INDEX_HEX[fameIndexColor(value)];
}

export function indexPillBg(value: number | null): string {
  return INDEX_PILL_BG[fameIndexColor(value)];
}

export function indexPillFg(value: number | null): string {
  return INDEX_PILL_FG[fameIndexColor(value)];
}

/** Colori del FameRisk per livello 1-5. */
export const RISK_HEX: Record<number, string> = {
  1: '#2A9D8F',
  2: '#97C459',
  3: '#EF9F27',
  4: '#D85A30',
  5: '#A32D2D',
};

export const RISK_PILL_BG: Record<number, string> = {
  1: 'var(--green-light)',
  2: 'var(--green-light)',
  3: 'var(--amber-light)',
  4: 'var(--red-light)',
  5: 'var(--red-light)',
};

export const RISK_PILL_FG: Record<number, string> = {
  1: 'var(--green-dark)',
  2: 'var(--green-dark)',
  3: '#8B6914',
  4: 'var(--red-dark)',
  5: 'var(--red-dark)',
};

export const SIGNAL_QUALITY: Record<string, { dot: string; color: string }> = {
  solid: { dot: 'solid', color: '#2A9D8F' },
  partial: { dot: 'partial', color: '#EF9F27' },
  insufficient: { dot: 'insufficient', color: '#A32D2D' },
};

/**
 * Numero con virgola decimale italiana e `decimals` cifre, come il riferimento
 * stampa valori e medie anche in interfaccia inglese.
 */
export function formatDecimal(n: number, decimals = 1): string {
  return n.toFixed(decimals).replace('.', ',');
}

/**
 * `fmt` — importi in euro abbreviati come nel riferimento: €1,5M / €500K / €123.
 * La virgola decimale è quella italiana anche in interfaccia inglese, perché è
 * esattamente quello che fa l'HTML congelato.
 */
export function formatEuro(n: number): string {
  // Nessun ramo per `null`: lo schema dichiara gli euro come interi non nulli
  // (`z.int().min(0)`), quindi il caso non è raggiungibile e il suo "N.D."
  // scritto fisso era una stringa non tradotta in codice morto.
  if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (n >= 1000) return `€${(n / 1000).toFixed(0)}K`;
  return `€${n}`;
}

/**
 * Sfondo della pillola FameIntegrity nella classifica del Level 3.
 *
 * Attenzione: NON coincide con `integrityPillBg`, usato nelle card indice del
 * Level 1. Il riferimento adotta tre valori diversi su quattro fra i due
 * contesti, ed è una sua scelta: uniformarli sarebbe uno scostamento.
 *
 *   fascia        classifica   card indice
 *   < 20          #FDF0EE      #FDF0EE
 *   20-49         #FBE9E5      #FDE8E0
 *   50-79         #FEF5E0      #FFF8E6
 *   >= 80         #E8F6F4      #E8F5F0
 */
const RANKING_INTEGRITY_BG: Record<number, string> = {
  [-1]: '#F0EFEC',
  0: '#FDF0EE',
  1: '#FBE9E5',
  2: '#FEF5E0',
  3: '#E8F6F4',
};

export function rankingIntegrityBg(value: number | null): string {
  return RANKING_INTEGRITY_BG[fameIntegrityBand(value).tier] ?? '#F0EFEC';
}

/**
 * Classe del pallino di verdetto nella testata, da `classification.matrixColor`.
 *
 * Il riferimento interpola il valore grezzo (`brand-dot ${cl.matrixColor}`), e
 * per i tre valori che i dati usano — `green`, `red`, `blue` — questo è
 * esattamente ciò che facciamo.
 *
 * L'unica traduzione riguarda `amber`, che la Specifica §6 dichiara tollerato e
 * "mappato a orange". Il riferimento quella mappatura ce l'ha, alla lettera
 * (`cl.matrixColor === 'amber' ? 'orange' : …`), ma la applica al badge del
 * verdetto, che il v40 ha rimosso dalle testate: sul pallino è rimasto il valore
 * grezzo, e `.brand-dot.amber` nel CSS non esiste. Replicarlo qui vorrebbe dire
 * disegnare un pallino senza colore per un valore che la specifica dichiara
 * valido, quindi si applica la mappatura che il riferimento stesso scrive.
 *
 * Nessun brand del dataset usa `amber`: la resa di collaudo non cambia.
 */
export function brandDotClass(matrixColor: string | undefined): string {
  if (matrixColor === undefined) return 'gray';
  return matrixColor === 'amber' ? 'orange' : matrixColor;
}
